import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFocusTrap } from '../hooks/useReveal';
import { REGISTRATION_FORM_URL } from '../constants/links';
import CivicButton from './CivicButton';
import OfficialBanner from './OfficialBanner';
import './Navigation.css';

interface NavigationProps {
  logo: string;
}

const Navigation: React.FC<NavigationProps> = ({ logo }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const { ref: drawerRef, activate: activateTrap, deactivate: deactivateTrap } = useFocusTrap<HTMLDivElement>();

  // Focus trap: activate when drawer opens, deactivate when closes
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(activateTrap, 100);
      return () => { clearTimeout(timer); deactivateTrap(); };
    }
  }, [isOpen, activateTrap, deactivateTrap]);

  // Close drawer on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Detectar sección activa
      const sections = [
        { id: 'inicio', element: document.getElementById('inicio') },
        { id: 'quienes-somos', element: document.getElementById('quienes-somos') },
        { id: 'que-hacemos', element: document.getElementById('que-hacemos') },
        { id: 'valores', element: document.getElementById('valores') },
        { id: 'galeria', element: document.getElementById('galeria') },
        { id: 'slogan', element: document.getElementById('slogan') },
        { id: 'cumbre-cyc', element: document.getElementById('cumbre-cyc') },
        { id: 'ruta-nacional', element: document.getElementById('ruta-nacional') },
        { id: 'contacto', element: document.getElementById('contacto') }
      ];

      // Offset dinámico para detección de sección activa
      const navbarHeight = isScrolled ? 108 : 116;
      const scrollPosition = window.scrollY + navbarHeight + 50;
      let currentSection = 'inicio'; // Default

      // Verificar cada sección desde arriba hacia abajo
      for (const section of sections) {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          
          // Detectar si la sección está visible en el viewport
          if (scrollPosition >= elementTop - 50 && scrollPosition < elementTop + rect.height) {
            currentSection = section.id;
            break; // Usar la primera sección que coincida
          }
        }
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Llamar una vez al cargar
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

      const menuItems = [
        {
          label: 'Inicio',
          href: '#inicio'
        },
        {
          label: 'Quiénes Somos',
          href: '#quienes-somos'
        },
        {
          label: 'Qué Hacemos',
          href: '#que-hacemos'
        },
        {
          label: 'Valores',
          href: '#valores'
        },
        {
          label: 'Galería',
          href: '#galeria'
        },
        {
          label: 'Slogan',
          href: '#slogan'
        },
        {
          label: 'Cumbre CyC',
          href: '#cumbre-cyc'
        },
        {
          label: 'Ruta',
          href: '#ruta-nacional'
        },
        {
          label: 'Contacto',
          href: '#contacto'
        }
      ];

  const toggleMenu = () => setIsOpen(!isOpen);

  const scrollToSection = (href: string) => {
    setIsOpen(false);
    
    // Pequeño delay para permitir que el menú se cierre antes del scroll
    setTimeout(() => {
      if (href === '#inicio') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const element = document.querySelector(href);
        if (element) {
          // Offset dinámico basado en el tamaño de la navbar
          const navbarHeight = isScrolled ? 108 : 116;
          const mobileOffset = window.innerWidth <= 768 ? navbarHeight + 20 : navbarHeight;
          
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - mobileOffset;

          // Función de scroll mejorada para móviles
          const scrollToPosition = (position: number) => {
            // Verificar si el navegador soporta smooth scroll
            if ('scrollBehavior' in document.documentElement.style) {
              window.scrollTo({
                top: Math.max(0, position),
                behavior: 'smooth'
              });
            } else {
              // Fallback para navegadores que no soportan smooth scroll
              const startPosition = window.pageYOffset;
              const distance = position - startPosition;
              const duration = 500;
              let start: number | null = null;

              const animation = (currentTime: number) => {
                if (start === null) start = currentTime;
                const timeElapsed = currentTime - start;
                const run = easeInOutQuad(timeElapsed, startPosition, distance, duration);
                window.scrollTo(0, run);
                if (timeElapsed < duration) requestAnimationFrame(animation);
              };

              requestAnimationFrame(animation);
            }
          };

          scrollToPosition(offsetPosition);
        }
      }
    }, 150); // Aumentar delay para móviles
  };

  // Función de easing para animación suave (fallback)
  const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
    t /= d / 2;
    if (t < 1) return c / 2 * t * t + b;
    t--;
    return -c / 2 * (t * (t - 2) - 1) + b;
  };

  return (
    <div className={`site-chrome ${isScrolled ? 'is-scrolled' : ''}`}>
      <OfficialBanner />
      <nav className={`navigation ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navigation__container">
        <a
          href="#inicio"
          className="navigation__logo"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection('#inicio');
          }}
        >
          <img src={logo} alt="Una Voz Diferente" />
        </a>

        {/* Mobile Menu Button */}
        <button 
          className="navigation__mobile-button"
          onClick={toggleMenu}
          aria-label={isOpen ? "Cerrar menú" : "Abrir menú"}
        >
          {isOpen ? <X /> : <Menu />}
        </button>

        <div className="navigation__desktop-menu">
          {menuItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className={`navigation__link ${activeSection === item.href.substring(1) ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(item.href);
              }}
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="navigation__cta">
          <CivicButton
            href={REGISTRATION_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
          >
            Inscríbete
          </CivicButton>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div 
              className="navigation__mobile-menu"
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label="Menú de navegación móvil"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              {menuItems.map((item) => (
                <a 
                  key={item.label}
                  href={item.href}
                  className={`navigation__mobile-link ${activeSection === item.href.substring(1) ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.href);
                    setIsOpen(false); // Cerrar menú móvil al hacer clic
                  }}
                >
                  {item.label}
                </a>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
    </div>
  );
};

export default Navigation;